import Alert from "@material-ui/lab/Alert";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  BooleanInput,
  FormGroupContextProvider,
  FormWithRedirect,
  maxValue,
  minValue,
  NumberInput,
  required,
  Title,
  useNotify,
  useUpdate,
} from "react-admin";

import CustomCard from "../../components/CustomCard";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import Loader from "../../components/Loader";
import Spacing from "../../components/Spacing";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import { firestore } from "../../init/firebase";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { AppResource } from "../../types/resource.types";
import { applyUpdateTimestamp } from "../../utils/timestamp";
import LocalizedRichText from "./LocalizedRichText";

const Settings: FC<any> = (props) => {
  const [update, { loading }] = useUpdate();
  const notify = useNotify();

  const [settings, setSettings] = useState<any>({});
  const [fetchingSettingsError, setFetchingSettingsError] = useState<string | null>(null);
  const [fetchingSettingsLoading, setFetchingSettingsLoading] = useState(true);

  const [contactUsHasError, setContactUsHasError] = useState(false);
  const [termsHasError, setTermsHasError] = useState(false);
  const [aboutHasError, setAboutHasError] = useState(false);

  const contactUs = useRef({ en: "", ar: "" });
  const terms = useRef({ en: "", ar: "" });
  const about = useRef({ en: "", ar: "" });

  const { disabledStyles } = useDisabledOnLoading(loading);

  const handleContactUsChange = useCallback((value: any) => {
    contactUs.current = value;
  }, []);

  const handleTermsChange = useCallback((value: any) => {
    terms.current = value;
  }, []);

  const handleAboutChange = useCallback((value: any) => {
    about.current = value;
  }, []);

  const handleSubmit = (data: any) => {
    const { ios, android, ...finalData } = data;

    update(
      AppResource.settings,
      AppResource.general,
      applyUpdateTimestamp({
        ...finalData,
        maintenance: { ios, android },
        contactUs: contactUs.current,
        about: about.current,
        terms: terms.current,
      }),
      settings,
      {
        onSuccess() {
          notify("Settings has been updated", "success");
        },
        onFailure(ex: any) {
          console.log(ex.message);
          notify("Error on updating settings", "error");
        },
      }
    );
  };

  useEffect(() => {
    setFetchingSettingsLoading(true);
    firestore
      .collection(AppResource.settings)
      .doc("general")
      .get()
      .then((doc) => {
        setSettings(doc.data());
        contactUs.current = doc.data()?.contactUs;
        terms.current = doc.data()?.terms;
        about.current = doc.data()?.about;
        setFetchingSettingsLoading(false);
        setFetchingSettingsError(null);
      })
      .catch(() => {
        setFetchingSettingsLoading(false);
        setFetchingSettingsError("Couldn't fetch settings!");
      });
  }, []);

  return (
    <Loader loading={fetchingSettingsLoading} size={24}>
      {fetchingSettingsError ? (
        <Alert severity="error">{fetchingSettingsError}</Alert>
      ) : (
        <FormWrapper>
          <Title title="Settings" />
          <FormGroupContextProvider name={FormGroupNames.settings}>
            <FormWithRedirect
              {...props}
              record={settings}
              save={handleSubmit}
              render={(formProps) => {
                return (
                  <form style={disabledStyles}>
                    <CustomCard title="Terms">
                      <LocalizedRichText
                        onChange={handleTermsChange}
                        hasError={(error) => {
                          setTermsHasError(error);
                        }}
                        initialValue={settings.terms}
                      />
                    </CustomCard>

                    <CustomCard title="About">
                      <LocalizedRichText
                        onChange={handleAboutChange}
                        hasError={(error) => {
                          setAboutHasError(error);
                        }}
                        initialValue={settings.about}
                      />
                    </CustomCard>

                    <CustomCard title="Contact Us">
                      <LocalizedRichText
                        onChange={handleContactUsChange}
                        hasError={(error) => {
                          setContactUsHasError(error);
                        }}
                        initialValue={settings.contactUs}
                      />
                    </CustomCard>

                    <CustomCard title="General">
                      <Spacing>
                        <NumberInput
                          source="sportClubSearchRadius"
                          validate={[required(), minValue(0)]}
                          variant="outlined"
                          fullWidth
                          label="Sport club search radius (KM)"
                        />
                        <NumberInput
                          source="maxReservationSlots"
                          validate={[required(), minValue(0)]}
                          variant="outlined"
                          fullWidth
                        />
                      </Spacing>

                      <Spacing>
                        <NumberInput
                          source="maxCartItems"
                          validate={[required(), minValue(0)]}
                          variant="outlined"
                          fullWidth
                        />
                        <NumberInput
                          source="hotOffersPercentage"
                          validate={[required(), minValue(0), maxValue(100)]}
                          variant="outlined"
                          fullWidth
                        />
                      </Spacing>

                      <Spacing>
                        <NumberInput
                          source="membersSearchRadius"
                          label="Members search radius (Km)"
                          validate={[required(), minValue(0)]}
                          variant="outlined"
                          fullWidth
                        />
                        <NumberInput
                          source="challengeMaxAge"
                          label="Challenge slot hold (Hr)"
                          validate={[required(), minValue(0), maxValue(100)]}
                          variant="outlined"
                          fullWidth
                        />
                      </Spacing>

                      <Spacing>
                        <NumberInput
                          source="paymentWaitingTime"
                          label="Payment waiting time (Min)"
                          validate={[required(), minValue(1)]}
                          variant="outlined"
                          fullWidth
                        />
                        <NumberInput
                          source="entertainmentRequestMaxAge"
                          label="Entertainment request max age (Min)"
                          validate={[required(), minValue(1)]}
                          variant="outlined"
                          fullWidth
                        />
                      </Spacing>

                      <BooleanInput source="forceUpdate" variant="outlined" fullWidth defaultValue={false} />
                    </CustomCard>

                    <CustomCard title="Maintenance status">
                      <Spacing>
                        <BooleanInput source="ios" variant="outlined" fullWidth defaultValue={false} />
                        <BooleanInput source="android" variant="outlined" fullWidth defaultValue={false} />
                      </Spacing>
                    </CustomCard>

                    <CustomToolbar
                      {...formProps}
                      formName={FormGroupNames.settings}
                      customErrors={{
                        contactUs: contactUsHasError ? "Contact us is required" : "",
                        terms: termsHasError ? "Terms is required" : "",
                        about: aboutHasError ? "about is required" : "",
                      }}
                    />
                  </form>
                );
              }}
            />
          </FormGroupContextProvider>
        </FormWrapper>
      )}
    </Loader>
  );
};

export default Settings;
